/*
 * surround-engine.js — shared discrete-multichannel speaker test engine.
 *
 * THE PROBLEM IT SOLVES
 * ---------------------
 * Naive "surround test" tools fake position with a StereoPanner / HRTF panner.
 * That always collapses to TWO channels. On a real 5.1/7.1 system the receiver
 * plays those two channels through the FRONT speakers only — the physical side,
 * rear and height speakers never activate. (Reported on /speaker-positioning-guide,
 * 2026-06-29: "5.1 и 7.1 боковые и задние динамики не активны, звучат фронтальные".)
 *
 * THE FIX
 * -------
 * When the OS output device actually exposes >= N channels
 * (destination.maxChannelCount), drive each test signal to a SPECIFIC physical
 * channel index via a ChannelMergerNode with channelInterpretation='discrete'.
 * Click "Side Left" -> only the physical Side-Left speaker plays.
 *
 * When the device is stereo-only (laptop, phone, OS output set to "Stereo"),
 * discrete surround is physically impossible. We fall back to an HONEST binaural
 * HRTF simulation (good on headphones) and tell the user, in plain words, to set
 * their system audio output to the 5.1/7.1 device for a real physical test.
 *
 * Channel order = the Windows WAVE_FORMAT_EXTENSIBLE bitmask order, which is what
 * Chromium emits for Web-Audio output (media/base/channel_layout.h: "the channel
 * order matches the order of the bitmask in the Windows WAVEFORMATEXTENSIBLE
 * format"). Verified against the W3C Web Audio spec, MS KSAUDIO_SPEAKER masks and
 * Apple CoreAudio layout tags (independent dual research + adversarial verify,
 * 2026-06-29):
 *   5.1 (6ch): 0=FL 1=FR 2=FC 3=LFE 4=SL(surround) 5=SR   (== W3C 'speakers' 5.1)
 *   7.1 (8ch): 0=FL 1=FR 2=FC 3=LFE 4=BL(rear) 5=BR 6=SL(side) 7=SR
 * NB: BACK comes before SIDE (back mask bits 0x10/0x20 < side bits 0x200/0x400).
 * This is the browser->OS direct path; an FFmpeg/ITU muxer in between would use a
 * different (side-at-4/5) order, but there is none here.
 *
 * Caveats (research): mobile is stereo-only (maxChannelCount=2 -> binaural fallback);
 * Chromium desktop is the reliable discrete path; Firefox has recurring stereo-
 * downmix regressions and Safari desktop generally clamps to stereo — we can only
 * trust maxChannelCount, so on those a "real" badge may overstate. Acceptable: the
 * fallback is honest and the reported case (Opera/Chromium/Windows) is covered.
 *
 * The engine is dependency-injected on the AudioContext so the exact same code
 * path can be rendered offline (OfflineAudioContext) and channel-isolation tested.
 */
(function (global) {
    'use strict';

    // setup -> { channels, idx: { speakerId: physicalChannelIndex }, heightIds? }
    var LAYOUTS = {
        'stereo': { channels: 2, idx: { L: 0, R: 1 } },
        '5.1': { channels: 6, idx: { L: 0, R: 1, C: 2, SUB: 3, SL: 4, SR: 5 } },
        '7.1': { channels: 8, idx: { L: 0, R: 1, C: 2, SUB: 3, SBL: 4, SBR: 5, SL: 6, SR: 7 } },
        // 7.1.4: the 8-channel bed routes discretely; the 4 height speakers have NO
        // consumer PCM path from a browser (ceiling speakers need an Atmos bitstream),
        // so they always play a clearly-labelled binaural cue.
        '7.1.4': {
            channels: 8,
            idx: { L: 0, R: 1, C: 2, SUB: 3, SBL: 4, SBR: 5, SL: 6, SR: 7 },
            heightIds: ['TFL', 'TFR', 'TRL', 'TRR'],
        },
    };

    // Azimuth (deg, 0=front, +=right) / elevation (deg) for the binaural fallback panner.
    var POS = {
        L: { az: -30, el: 0 }, R: { az: 30, el: 0 }, C: { az: 0, el: 0 }, SUB: { az: 0, el: -8 },
        SL: { az: -90, el: 0 }, SR: { az: 90, el: 0 }, SBL: { az: -150, el: 0 }, SBR: { az: 150, el: 0 },
        TFL: { az: -45, el: 45 }, TFR: { az: 45, el: 45 }, TRL: { az: -135, el: 45 }, TRR: { az: 135, el: 45 },
    };

    function maxChannels(ctx) {
        try { return ctx.destination.maxChannelCount || 2; } catch (e) { return 2; }
    }

    // What can this context actually do for this setup?
    //   mode: 'discrete' -> real physical channels; 'binaural' -> simulation (stereo device)
    function capabilities(ctx, setup) {
        var layout = LAYOUTS[setup] || LAYOUTS.stereo;
        var max = maxChannels(ctx);
        var canDiscrete = max >= layout.channels;
        return {
            setup: setup,
            mode: canDiscrete ? 'discrete' : 'binaural',
            needChannels: layout.channels,
            maxChannels: max,
            layout: layout,
        };
    }

    // ── Signal generators (energy on demand; reused per speaker) ──────────────
    function pinkNoiseBuffer(ctx, seconds) {
        var len = Math.floor(ctx.sampleRate * seconds);
        var buf = ctx.createBuffer(1, len, ctx.sampleRate);
        var d = buf.getChannelData(0);
        var b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
        for (var i = 0; i < len; i++) {
            var w = Math.random() * 2 - 1;
            b0 = 0.99886 * b0 + w * 0.0555179;
            b1 = 0.99332 * b1 + w * 0.0750759;
            b2 = 0.96900 * b2 + w * 0.1538520;
            b3 = 0.86650 * b3 + w * 0.3104856;
            b4 = 0.55000 * b4 + w * 0.5329522;
            b5 = -0.7616 * b5 - w * 0.0168980;
            d[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
            b6 = w * 0.115926;
        }
        return buf;
    }

    // Build the per-speaker source chain up to (but not including) the spatial stage.
    // opts.signal: 'tone' -> pure sine (opts.freq||440); 'buffer' -> opts.buffer looped
    // (a spoken speaker name, /speaker-test); else role-shaped pink noise.
    // Returns { out: AudioNode, sources: [{stop}], isHeight: bool }
    function buildSource(ctx, speakerId, opts) {
        opts = opts || {};
        var sources = [];
        var isHeight = speakerId.charAt(0) === 'T';
        // A spoken name cannot pass a subwoofer's crossover, so SUB keeps its LFE
        // sweep below whatever the caller asked for.
        if (speakerId !== 'SUB' && opts.signal === 'buffer' && opts.buffer) {
            var vb = ctx.createBufferSource();
            vb.buffer = opts.buffer;
            vb.loop = true;
            var vg = ctx.createGain();
            vg.gain.value = 1;
            vb.connect(vg);
            vb.start();
            sources.push({ stop: function () { try { vb.stop(); } catch (e) {} } });
            return { out: vg, sources: sources, isHeight: isHeight };
        }
        if (speakerId !== 'SUB' && opts.signal === 'tone') {
            var to = ctx.createOscillator();
            to.type = 'sine';
            to.frequency.value = opts.freq || 440;
            var tg = ctx.createGain();
            tg.gain.value = 0.5;
            to.connect(tg);
            to.start();
            sources.push({ stop: function () { try { to.stop(); } catch (e) {} } });
            return { out: tg, sources: sources, isHeight: isHeight };
        }
        if (speakerId === 'SUB') {
            // LFE: 30 -> 80 -> 30 Hz sine sweep, omnidirectional.
            var osc = ctx.createOscillator();
            osc.type = 'sine';
            var t0 = ctx.currentTime;
            osc.frequency.setValueAtTime(30, t0);
            osc.frequency.linearRampToValueAtTime(80, t0 + 4);
            osc.frequency.linearRampToValueAtTime(30, t0 + 8);
            var sg = ctx.createGain();
            sg.gain.value = 0.6;
            osc.connect(sg);
            osc.start();
            sources.push({ stop: function () { try { osc.stop(); } catch (e) {} } });
            return { out: sg, sources: sources, isHeight: false };
        }
        // Everyone else: looped pink noise, band-shaped per role so channels are distinguishable.
        var src = ctx.createBufferSource();
        src.buffer = pinkNoiseBuffer(ctx, 2);
        src.loop = true;
        var bp = ctx.createBiquadFilter();
        bp.type = 'bandpass';
        if (speakerId === 'C') { bp.frequency.value = 1100; bp.Q.value = 0.7; }       // dialog band
        else if (isHeight) { bp.frequency.value = 4500; bp.Q.value = 0.6; }            // height = bright
        else { bp.frequency.value = 2500; bp.Q.value = 0.6; }                          // surround/front
        src.connect(bp);
        src.start();
        sources.push({ stop: function () { try { src.stop(); } catch (e) {} } });
        return { out: bp, sources: sources, isHeight: isHeight };
    }

    // HRTF panner for the binaural path.
    function makePanner(ctx, speakerId) {
        var pos = POS[speakerId] || { az: 0, el: 0 };
        var panner = ctx.createPanner();
        panner.panningModel = 'HRTF';
        panner.distanceModel = 'inverse';
        panner.rolloffFactor = 0;
        var azRad = pos.az * Math.PI / 180;
        var elRad = pos.el * Math.PI / 180;
        var r = 2;
        var x = Math.sin(azRad) * Math.cos(elRad) * r;
        var y = Math.sin(elRad) * r;
        var z = -Math.cos(azRad) * Math.cos(elRad) * r; // -z = in front of listener
        if (panner.positionX) {
            panner.positionX.value = x; panner.positionY.value = y; panner.positionZ.value = z;
        } else if (panner.setPosition) {
            panner.setPosition(x, y, z); // legacy Safari
        }
        return panner;
    }

    /*
     * Start a test for ONE speaker. Dependency-injected on ctx + final destination
     * node so it renders identically live and offline.
     *
     * opts: { ctx, dest, setup, speakerId, gain }
     * returns { stop(), mode }   (mode = how this speaker was actually played)
     */
    function play(opts) {
        var ctx = opts.ctx;
        var dest = opts.dest || ctx.destination;
        var setup = opts.setup;
        var speakerId = opts.speakerId;
        var gainVal = (opts.gain == null) ? 0.85 : opts.gain;
        var cap = capabilities(ctx, setup);
        var layout = cap.layout;
        var isHeightOnly = layout.heightIds && layout.heightIds.indexOf(speakerId) !== -1;

        var built = buildSource(ctx, speakerId, { signal: opts.signal, freq: opts.freq, buffer: opts.buffer });
        var master = ctx.createGain();
        master.gain.value = gainVal;

        var disposables = [master];
        var mode;

        if (cap.mode === 'discrete' && !isHeightOnly && layout.idx[speakerId] != null) {
            // ── Real physical channel routing ───────────────────────────────
            mode = 'discrete';
            var n = layout.channels;
            try {
                dest.channelCount = Math.min(n, maxChannels(ctx));
                dest.channelCountMode = 'explicit';
                dest.channelInterpretation = 'discrete';
            } catch (e) { /* OfflineAudioContext dest channelCount is fixed; ignore */ }
            var merger = ctx.createChannelMerger(n);
            disposables.push(merger);
            built.out.connect(master);
            master.connect(merger, 0, layout.idx[speakerId]); // -> exact physical channel
            merger.connect(dest);
        } else {
            // ── Honest binaural simulation (stereo device, or height channel) ──
            mode = isHeightOnly && cap.mode === 'discrete' ? 'binaural_height' : 'binaural';
            var panner = makePanner(ctx, speakerId);
            disposables.push(panner);
            if (speakerId === 'SUB') {
                // sub has no useful HRTF cue — center it
                built.out.connect(master);
            } else {
                built.out.connect(panner);
                panner.connect(master);
            }
            // Fold to a stereo pair of the destination (indices 0,1) so it is audible
            // even when dest happens to be a wide discrete bus.
            if (cap.mode === 'discrete') {
                var fold = ctx.createChannelMerger(layout.channels);
                disposables.push(fold);
                var split = ctx.createChannelSplitter(2);
                disposables.push(split);
                master.connect(split);
                split.connect(fold, 0, 0);
                split.connect(fold, 1, 1);
                try {
                    dest.channelCount = Math.min(layout.channels, maxChannels(ctx));
                    dest.channelCountMode = 'explicit';
                    dest.channelInterpretation = 'discrete';
                } catch (e) {}
                fold.connect(dest);
            } else {
                master.connect(dest);
            }
        }

        var stopped = false;
        function stop() {
            if (stopped) return;
            stopped = true;
            try {
                master.gain.setValueAtTime(master.gain.value, ctx.currentTime);
                master.gain.linearRampToValueAtTime(0.0001, ctx.currentTime + 0.06);
            } catch (e) {}
            setTimeout(function () {
                built.sources.forEach(function (s) { s.stop(); });
                disposables.forEach(function (nd) { try { nd.disconnect(); } catch (e) {} });
            }, 90);
        }

        return { stop: stop, mode: mode };
    }

    global.SurroundEngine = {
        LAYOUTS: LAYOUTS,
        POS: POS,
        maxChannels: maxChannels,
        capabilities: capabilities,
        buildSource: buildSource,
        play: play,
    };
})(typeof window !== 'undefined' ? window : globalThis);
